import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../../../services';
import { utils } from '../../../utils';
import { ApiResponse } from '../../../types/api';
import { Config } from '../../../services/config';
import FormLayout from '../../../components/Common/FormLayout';

interface ChecklistFormData {
  id: number;
  veiculo_id: number | null;
  veiculo_placa: string;
  equipamento_id: number | null;
  equipamento_tag: string;
  equipamento_nome: string;
  motorista_id: number;
  motorista_nome: string;
  tipo: string;
  status: 'pendente' | 'concluido' | 'reprovado';
  data_realizacao: string;
  observacoes?: string;
  quilometragem: number;
  itens: ChecklistItem[];
}

interface ChecklistItem {
  id: number;
  originalItemId?: number; // ID do item padrão original
  descricao: string;
  categoria: string;
  obrigatorio: boolean;
  valor: 'aprovado' | 'reprovado' | 'pendente';
  observacao?: string;
}

const ChecklistEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [checklistEntityType, setChecklistEntityType] = useState<'veiculo' | 'equipamento'>('veiculo');
  const [formData, setFormData] = useState<ChecklistFormData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newItems, setNewItems] = useState<ChecklistItem[]>([]);
  const [removedItems, setRemovedItems] = useState<number[]>([]);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [defaultItems, setDefaultItems] = useState<ChecklistItem[]>([]);
  const [isLoadingDefaultItems, setIsLoadingDefaultItems] = useState(false);

  useEffect(() => {
    loadChecklist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Função para mapear itens padrão já usados baseado em descrição e categoria
  const getUsedDefaultItemIds = (existingItems: ChecklistItem[], defaultItems: ChecklistItem[]): number[] => {
    const usedIds: number[] = [];

    existingItems.forEach(existingItem => {
      // Para itens que têm originalItemId (novos), use diretamente
      if (existingItem.originalItemId) {
        usedIds.push(existingItem.originalItemId);
        return;
      }

      // Para itens existentes (carregados da API), encontre o correspondente nos itens padrão
      const matchingDefaultItem = defaultItems.find(defaultItem =>
        defaultItem.descricao === existingItem.descricao &&
        defaultItem.categoria === existingItem.categoria
      );

      if (matchingDefaultItem) {
        usedIds.push(matchingDefaultItem.id);
      }
    });

    return usedIds;
  };

  const loadChecklist = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Carregar checklist e itens padrão
      const checklistResponse = await apiService.getChecklist(Number(id)) as ApiResponse<any>;
      const defaultItemsResponse = await apiService.getChecklistItemsDefault() as ApiResponse<any>;

      if (checklistResponse.success && checklistResponse.data) {
        const apiChecklist = checklistResponse.data as any;

        // Carregar itens padrão para mapeamento
        let defaultItems: ChecklistItem[] = [];
        if (defaultItemsResponse.success && defaultItemsResponse.data) {
          defaultItems = defaultItemsResponse.data as ChecklistItem[];
        }

        // Converter motorista_id para o formato esperado
        const motoristaIdRaw = apiChecklist.motorista_id;
        let motoristaId = 0;
        if (motoristaIdRaw !== null && motoristaIdRaw !== undefined && motoristaIdRaw !== '') {
          motoristaId = Number(motoristaIdRaw);
          // Se a conversão resultar em NaN, usar 0
          if (isNaN(motoristaId)) {
            motoristaId = 0;
          }
        }

        const existingItems: ChecklistItem[] = apiChecklist.itens.map((item: any) => ({
          id: Number(item.id),
          descricao: item.descricao || item.nome,
          categoria: item.categoria || item.grupo,
          obrigatorio: Boolean(item.obrigatorio),
          valor: item.valor === 'conforme' ? 'aprovado' :
                 item.valor === 'nao_conforme' ? 'reprovado' :
                 item.valor || (item.status === 'ok' ? 'aprovado' : item.status === 'nok' ? 'reprovado' : 'pendente'),
          observacao: item.observacao || item.observacoes
        }));

        // Detectar o tipo de checklist (veículo ou equipamento)
        const isEquipmentChecklist = apiChecklist.equipamento_id != null;
        const isVehicleChecklist = apiChecklist.veiculo_id != null;

        if (isEquipmentChecklist) {
          setChecklistEntityType('equipamento');
        } else if (isVehicleChecklist) {
          setChecklistEntityType('veiculo');
        }

        const convertedChecklist: ChecklistFormData = {
          id: Number(apiChecklist.id),
          veiculo_id: apiChecklist.veiculo_id ? Number(apiChecklist.veiculo_id) : null,
          veiculo_placa: apiChecklist.veiculo_placa || '',
          equipamento_id: apiChecklist.equipamento_id ? Number(apiChecklist.equipamento_id) : null,
          equipamento_tag: apiChecklist.equipamento_tag || '',
          equipamento_nome: apiChecklist.equipamento_nome || '',
          motorista_id: motoristaId,
          motorista_nome: apiChecklist.motorista_nome,
          tipo: apiChecklist.tipo,
          status: apiChecklist.status,
          data_realizacao: apiChecklist.data_realizacao,
          observacoes: apiChecklist.observacoes,
          quilometragem: Number(apiChecklist.odometro),
          itens: existingItems
        };

        setDefaultItems(defaultItems);
        setFormData(convertedChecklist);
      } else {
        throw new Error(checklistResponse.message || 'Erro ao carregar checklist');
      }
    } catch (error) {
      console.error('Erro ao carregar checklist:', error);
      setError('Erro ao carregar checklist para edição');
      utils.showNotification('Erro ao carregar checklist para edição', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => prev ? { ...prev, [name]: value } : null);
  };

  const handleItemChange = (itemId: number, field: string, value: string) => {
    setFormData(prev => {
      if (!prev) return null;

      return {
        ...prev,
        itens: prev.itens.map(item =>
          item.id === itemId ? { ...item, [field]: value } : item
        )
      };
    });
  };

  const openAddItemModal = async () => {
    setShowAddItemModal(true);
    if (defaultItems.length === 0) {
      await loadDefaultItems();
    }
  };

  const loadDefaultItems = async () => {
    try {
      setIsLoadingDefaultItems(true);
      const response = await apiService.getChecklistItemsDefault() as ApiResponse<any>;

      if (response.success && response.data) {
        setDefaultItems(response.data);
      } else {
        utils.showNotification('Erro ao carregar itens padrão', { type: 'error' });
      }
    } catch (error) {
      console.error('Erro ao carregar itens padrão:', error);
      utils.showNotification('Erro ao carregar itens padrão', { type: 'error' });
    } finally {
      setIsLoadingDefaultItems(false);
    }
  };

  const addSelectedItems = (selectedItems: ChecklistItem[]) => {
    const newChecklistItems: ChecklistItem[] = selectedItems.map(item => ({
      id: -Date.now() - Math.random(), // IDs únicos negativos
      originalItemId: item.id, // Rastreia o ID do item padrão original
      descricao: item.descricao,
      categoria: item.categoria,
      obrigatorio: item.obrigatorio,
      valor: 'pendente' as const, // ✅ Padrão correto: Itens novos começam como pendente
      observacao: '' // Inicia sempre em branco para preenchimento manual
    }));

    setNewItems(prev => [...prev, ...newChecklistItems]);
    setFormData(prev => prev ? { ...prev, itens: [...prev.itens, ...newChecklistItems] } : null);
    // Modal permanece aberto para permitir seleção de mais itens
    // setShowAddItemModal(false);
  };

  const removeItem = (itemId: number) => {
    // Verificar se é um item existente ou um item novo
    if (itemId > 0) {
      // Item existente - adicionar à lista de removidos
      setRemovedItems(prev => [...prev, itemId]);
    } else {
      // Item novo - remover da lista de novos items
      setNewItems(prev => prev.filter(item => item.id !== itemId));
    }

    // Remover o item do formData
    setFormData(prev => prev ? {
      ...prev,
      itens: prev.itens.filter(item => item.id !== itemId)
    } : null);
  };

  const updateNewItem = (itemId: number, field: string, value: string | boolean) => {
    setNewItems(prev => prev.map(item =>
      item.id === itemId ? { ...item, [field]: value } : item
    ));

    setFormData(prev => {
      if (!prev) return null;

      return {
        ...prev,
        itens: prev.itens.map(item =>
          item.id === itemId ? { ...item, [field]: value } : item
        )
      };
    });
  };

  const handleSave = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    if (!formData) return;

    try {
      setIsSaving(true);

      // Array para armazenar erros durante as operações
      const failedUpdates: string[] = [];

      // Validar campos obrigatórios
      const hasVehicle = checklistEntityType === 'veiculo' && formData.veiculo_id;
      const hasEquipment = checklistEntityType === 'equipamento' && formData.equipamento_id;

      if (!hasVehicle && !hasEquipment) {
        const entityLabel = checklistEntityType === 'veiculo' ? 'veículo' : 'equipamento';
        utils.showNotification(`Preencha o campo obrigatório: ${entityLabel}`, { type: 'error' });
        return;
      }

      const missingFields = [];

      if (!formData.motorista_id || formData.motorista_id === 0) {
        missingFields.push('motorista');
      }

      if (!formData.tipo) {
        missingFields.push('tipo de checklist');
      }

      if (!formData.data_realizacao) {
        missingFields.push('data e hora');
      }

      if (missingFields.length > 0) {
        utils.showNotification(`Preencha os campos obrigatórios: ${missingFields.join(', ')}`, { type: 'error' });
        return;
      }

      // Validar campos obrigatórios dos novos itens
      const newItemsInvalid = newItems.filter(item =>
        !item.descricao || item.descricao.trim() === '' ||
        !item.categoria || item.categoria.trim() === ''
      );

      if (newItemsInvalid.length > 0) {
        utils.showNotification(
          'Todos os novos itens devem ter descrição e categoria preenchidas',
          { type: 'error' }
        );
        return;
      }

      // Validar observações obrigatórias para itens reprovados
      const missingObservations = formData.itens
        .filter(item => item.valor === 'reprovado' && (!item.observacao || item.observacao.trim() === ''))
        .map(item => item.descricao);

      if (missingObservations.length > 0) {
        utils.showNotification(
          `Itens reprovados devem ter observações: ${missingObservations.join(', ')}`,
          { type: 'error' }
        );
        return;
      }

      // DEBUG: Log da construção dos dados a enviar
      console.log('🔍 [DEBUG] FormData atual:', {
        tipo: formData.tipo,
        status: formData.status,
        data_realizacao: formData.data_realizacao,
        quilometragem: formData.quilometragem,
        observacoes: formData.observacoes,
        veiculo_id: formData.veiculo_id,
        motorista_id: formData.motorista_id
      });

      // Converter dados para o formato da API
      const checklistPrincipalData: any = {
        motorista_id: formData.motorista_id,
        tipo: formData.tipo,
        status: formData.status,
        data_realizacao: formData.data_realizacao,
        observacoes: formData.observacoes || '', // ✅ Enviar string vazia ao invés de null
        odometro: formData.quilometragem
      };

      // Adicionar veiculo_id ou equipamento_id baseado no tipo do checklist
      if (checklistEntityType === 'veiculo') {
        checklistPrincipalData.veiculo_id = formData.veiculo_id;
      } else {
        checklistPrincipalData.equipamento_id = formData.equipamento_id;
      }

      console.log('🚀 [DEBUG] Dados sendo enviados para update:', checklistPrincipalData);

      // Primeiro, atualizar os dados principais do checklist
      const checklistResponse = await apiService.updateChecklist(formData.id, checklistPrincipalData) as ApiResponse<any>;

      if (!checklistResponse.success) {
        const errorMsg = checklistResponse.message || 'Erro ao atualizar checklist';
        console.error('❌ Erro na atualização do checklist principal:', errorMsg);
        throw new Error(errorMsg);
      }

      // 2. Criar novos itens
      const failedNewItems: number[] = []; // Rastrear IDs de itens que falharam

      if (newItems.length > 0) {
        console.log('📋 Criando novos itens:', newItems);

        for (const newItem of newItems) {
          try {
            const itemData = {
              categoria: newItem.categoria,
              descricao: newItem.descricao,
              tipo: 'booleano', // Tipo padrão para novos itens
              obrigatorio: newItem.obrigatorio,
              valor: newItem.valor,
              observacao: newItem.observacao
            };

            const createResponse = await apiService.createChecklistItemForChecklist(formData.id, itemData) as any;

            if (createResponse.success) {
              // Backend retorna um array de itens criados diretamente no campo 'itens'
              const itensCreated = createResponse.itens || [];
              console.log('✅ Item criado:', itensCreated);
            } else {
              const errorMessage = createResponse.message || 'Erro ao criar item';
              console.error('❌ Erro ao criar item:', errorMessage);
              failedUpdates.push(`Novo item "${newItem.descricao}": ${errorMessage}`);
              failedNewItems.push(newItem.id); // Marcar como falho
            }
          } catch (itemError: any) {
            const errorMessage = itemError?.message || 'Erro desconhecido';
            console.error('❌ Exceção ao criar item:', itemError);
            failedUpdates.push(`Novo item "${newItem.descricao}": ${errorMessage}`);
            failedNewItems.push(newItem.id); // Marcar como falho
          }
        }
      }

      // 3. Atualizar itens existentes (não marcados para remoção)
      const itensExistentes = formData.itens.filter(item => item.id > 0 && !removedItems.includes(item.id));
      const failedUpdateItems: number[] = []; // Rastrear IDs de itens cuja atualização falhou

      if (itensExistentes.length > 0) {
        console.log('📋 Atualizando itens existentes:', itensExistentes);

        for (const item of itensExistentes) {
          try {
            const itemUpdateData = {
              valor: item.valor,
              observacao: item.observacao
            };

            const itemResponse = await apiService.updateChecklistItemInChecklist(
              formData.id,
              item.id,
              itemUpdateData
            ) as ApiResponse<any>;

            if (!itemResponse.success) {
              const errorMessage = itemResponse.message || 'Erro ao atualizar item';
              console.error(`❌ Erro ao atualizar item ${item.id}:`, errorMessage);
              failedUpdates.push(`Item ${item.id}: ${errorMessage}`);
              failedUpdateItems.push(item.id); // Marcar como falho
            } else {
              console.log(`✅ Item ${item.id} atualizado`);
            }
          } catch (itemError: any) {
            const errorMessage = itemError?.message || 'Erro desconhecido';
            console.error(`❌ Exceção ao atualizar item ${item.id}:`, itemError);
            failedUpdates.push(`Item ${item.id}: ${errorMessage}`);
            failedUpdateItems.push(item.id); // Marcar como falho
          }
        }
      }

      // 4. Remover itens marcados para remoção
      const failedRemovedItems: number[] = []; // Rastrear IDs de itens cuja remoção falhou

      if (removedItems.length > 0) {
        console.log('📋 Removendo itens:', removedItems);

        for (const itemIdToDelete of removedItems) {
          try {
            const deleteResponse = await apiService.deleteChecklistItemFromChecklist(formData.id, itemIdToDelete) as ApiResponse<any>;

            if (!deleteResponse.success) {
              const errorMessage = deleteResponse.message || 'Erro ao remover item';
              console.error(`❌ Erro ao remover item ${itemIdToDelete}:`, errorMessage);
              failedUpdates.push(`Remoção do item ${itemIdToDelete}: ${errorMessage}`);
              failedRemovedItems.push(itemIdToDelete); // Marcar como falho
            } else {
              console.log(`✅ Item ${itemIdToDelete} removido`);
            }
          } catch (itemError: any) {
            const errorMessage = itemError?.message || 'Erro desconhecido';
            console.error(`❌ Exceção ao remover item ${itemIdToDelete}:`, itemError);
            failedUpdates.push(`Remoção do item ${itemIdToDelete}: ${errorMessage}`);
            failedRemovedItems.push(itemIdToDelete); // Marcar como falho
          }
        }
      }

      console.log('📋 Operações concluídas:', {
        novosItens: newItems.length,
        itensAtualizados: itensExistentes.length,
        itensRemovidos: removedItems.length,
        erros: failedUpdates.length
      });

      // Verificar se houve falhas nas operações
      if (failedUpdates.length > 0) {
        const errorMsg = `Checklist atualizado, mas ${failedUpdates.length} operação(ões) falhou(aram): ${failedUpdates.join('; ')}`;
        utils.showNotification(errorMsg, { type: 'warning' });

        // Manter apenas os itens que falharam para o usuário poder tentar novamente
        setNewItems(prev => prev.filter(item => failedNewItems.includes(item.id)));

        // Manter apenas remoções que falharam
        setRemovedItems(failedRemovedItems);

        // Recarregar dados do backend sem resetar itens que falharam
        try {
          const checklistResponse = await apiService.getChecklist(Number(id)) as ApiResponse<any>;

          if (checklistResponse.success && checklistResponse.data) {
            const apiChecklist = checklistResponse.data as any;

            // Obter itens existentes do backend (já salvos com sucesso)
            let existingItemsFromBackend: ChecklistItem[] = apiChecklist.itens.map((item: any) => ({
              id: Number(item.id),
              descricao: item.descricao || item.nome,
              categoria: item.categoria || item.grupo,
              obrigatorio: Boolean(item.obrigatorio),
              valor: item.valor === 'conforme' ? 'aprovado' :
                     item.valor === 'nao_conforme' ? 'reprovado' :
                     item.valor || (item.status === 'ok' ? 'aprovado' : item.status === 'nok' ? 'reprovado' : 'pendente'),
              observacao: item.observacao || item.observacoes
            }));

            // Substituir itens que falharam ao atualizar com versão local (com edições do usuário)
            const itemsWithFailedUpdates = formData.itens.filter(item => failedUpdateItems.includes(item.id));
            existingItemsFromBackend = existingItemsFromBackend.map(backendItem => {
              const failedItem = itemsWithFailedUpdates.find(item => item.id === backendItem.id);
              return failedItem || backendItem; // Usa versão local se falhou, senão versão do backend
            });

            // Adicionar itens que falharam ao criar
            const itemsFailedToCreate = newItems.filter(item => failedNewItems.includes(item.id));

            setFormData(prev => prev ? {
              ...prev,
              itens: [...existingItemsFromBackend, ...itemsFailedToCreate]
            } : null);
          }
        } catch (reloadError) {
          console.error('❌ Erro ao recarregar checklist após falha parcial:', reloadError);
        }
      } else {
        utils.showNotification('Checklist e itens atualizados com sucesso!', { type: 'success' });

        // Limpar os arrays de novas modificações apenas se tudo foi salvo
        setNewItems([]);
        setRemovedItems([]);

        // Navegar apenas se tudo foi salvo com sucesso
        navigate(`/checklists/visualizar/${formData.id}`);
      }
    } catch (error) {
      console.error('Erro ao atualizar checklist:', error);
      utils.showNotification('Erro ao atualizar checklist', { type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const formatDateTime = (dateString: string): string => {
    if (!dateString) return '';
    // Se já está no formato correto (YYYY-MM-DDTHH:mm), retorna direto
    if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/)) {
      return dateString.slice(0, 16);
    }
    // Caso contrário, converte mantendo o horário local
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const parseDateTime = (dateTimeString: string): string => {
    if (!dateTimeString) return '';
    // Mantém o formato ISO local sem conversão para UTC
    if (!dateTimeString.includes('T')) {
      return dateTimeString;
    }
    // Garante que tenha segundos
    if (dateTimeString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/)) {
      return dateTimeString + ':00';
    }
    return dateTimeString;
  };

  if (isLoading) {
    return (
      <FormLayout
        title="Editando Checklist"
        subtitle="Carregando..."
        isLoading={true}
        onSave={() => {}}
        onCancel={() => navigate('/checklists')}
      >
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando checklist...</p>
        </div>
      </FormLayout>
    );
  }

  if (error || !formData) {
    return (
      <FormLayout
        title="Erro ao Carregar"
        subtitle={error || 'Checklist não encontrado'}
        isLoading={false}
        onSave={() => {}}
        onCancel={() => navigate('/checklists')}
        showActions={false}
      >
        <div style={{ textAlign: 'center', padding: '50px 20px' }}>
          <i className="fas fa-exclamation-triangle" style={{ fontSize: '3rem', color: '#e74c3c', marginBottom: '20px' }}></i>
          <h3>{error || 'Checklist não encontrado'}</h3>
          <p>O checklist solicitado não pôde ser carregado para edição.</p>
          <button className="btn btn-primary" onClick={() => navigate('/checklists')}>
            Voltar para Lista
          </button>
        </div>
      </FormLayout>
    );
  }

  return (
    <FormLayout
      title="Editando Checklist"
      subtitle={`Checklist #${formData.id}`}
      entityId={formData.id}
      isLoading={isSaving}
      onCancel={() => navigate('/checklists')}
      showActions={false}
    >
      <form onSubmit={handleSave}>
        {/* Informações Básicas */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Informações Básicas
          </h3>
          
          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            {checklistEntityType === 'veiculo' ? (
              <div className="form-group">
                <label className="form-label">Veículo *</label>
                <input
                  type="text"
                  name="veiculo_placa"
                  value={formData.veiculo_placa}
                  onChange={handleInputChange}
                  className="form-control"
                  disabled
                />
              </div>
            ) : (
              <div className="form-group">
                <label className="form-label">Equipamento *</label>
                <input
                  type="text"
                  name="equipamento_tag"
                  value={`${formData.equipamento_tag} - ${formData.equipamento_nome}`}
                  onChange={handleInputChange}
                  className="form-control"
                  disabled
                />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Motorista *</label>
              <input
                type="text"
                name="motorista_nome"
                value={formData.motorista_nome}
                onChange={handleInputChange}
                className="form-control"
                disabled
              />
            </div>

            <div className="form-group">
              <label className="form-label">Tipo de Checklist *</label>
              <select
                name="tipo"
                value={formData.tipo}
                onChange={handleInputChange}
                className="form-control"
              >
                <option value="pre_viagem">Pré-viagem</option>
                <option value="pos_viagem">Pós-viagem</option>
                <option value="diario">Diário</option>
                <option value="periodico">Periódico</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Data e Hora *</label>
              <input
                type="datetime-local"
                name="data_realizacao"
                value={formatDateTime(formData.data_realizacao)}
                onChange={(e) => setFormData(prev => prev ? { 
                  ...prev, 
                  data_realizacao: parseDateTime(e.target.value) 
                } : null)}
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                {checklistEntityType === 'veiculo' ? 'Quilometragem *' : 'Odômetro/Horímetro *'}
              </label>
              <input
                type="number"
                name="quilometragem"
                value={formData.quilometragem}
                onChange={handleInputChange}
                className="form-control"
                min="0"
                step="1"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Status *</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="form-control"
              >
                <option value="pendente">Pendente</option>
                <option value="concluido">Concluído</option>
                <option value="reprovado">Reprovado</option>
              </select>
            </div>
          </div>
        </div>

        {/* Observações */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Observações
          </h3>
          
          <div className="info-grid" style={{ gridTemplateColumns: '1fr' }}>
            <div className="form-group">
              <label className="form-label">Observações Gerais</label>
              <textarea
                name="observacoes"
                value={formData.observacoes || ''}
                onChange={handleInputChange}
                className="form-control"
                rows={4}
                placeholder="Digite observações gerais sobre o checklist..."
              />
            </div>
          </div>
        </div>

        {/* Itens do Checklist */}
        <div style={{ marginBottom: '30px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px', margin: 0 }}>
              Itens do Checklist ({formData.itens.length})
            </h3>


            <button
              type="button"
              onClick={openAddItemModal}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 20px',
                backgroundColor: '#04C64F',
                border: 'none',
                borderRadius: '6px',
                color: 'white',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 4px rgba(4, 198, 79, 0.3)',
                minWidth: '140px',
                justifyContent: 'center'
              }}
            >
              <i className="fas fa-plus"></i>
              Adicionar Item
            </button>
          </div>

          {/* Botão adicional quando não há itens */}
          {formData.itens.length === 0 && (
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <button
                type="button"
                onClick={openAddItemModal}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 24px',
                  backgroundColor: '#04C64F',
                  border: 'none',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 4px rgba(4, 198, 79, 0.3)',
                  minWidth: '200px',
                  justifyContent: 'center'
                }}
              >
                <i className="fas fa-plus"></i> Adicionar Itens do Checklist
              </button>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px' }}>
            {formData.itens.map(item => (
              <div
                key={item.id}
                className="card"
                style={{
                  padding: '16px',
                  paddingTop: '50px', // Espaçamento extra para o botão
                  position: 'relative',
                  borderLeft: item.id > 0 ? `4px solid ${Config.THEME.SUCCESS_COLOR}` : `4px solid ${Config.THEME.PRIMARY_COLOR}`
                }}
              >
                {/* Botão de remover - no topo direito do card */}
                <button
                  type="button"
                  onClick={() => removeItem(item.id)}
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: '#e74c3c',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    transition: 'all 0.2s ease',
                    zIndex: 10
                  }}
                  title="Remover item"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#c0392b';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#e74c3c';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  <i className="fas fa-times"></i>
                </button>

                {!!removedItems.find(removedId => removedId === item.id) && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(231, 76, 60, 0.1)',
                    border: '2px solid #e74c3c',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.9rem',
                    color: '#e74c3c',
                    fontWeight: 'bold'
                  }}>
                    ITEM SERÁ REMOVIDO
                  </div>
                )}

                <div className="form-group">
                  {item.id > 0 ? (
                    <label className="form-label" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                      {item.descricao}
                      {item.obrigatorio && (
                        <span style={{ color: '#e74c3c', marginLeft: '8px' }}>*</span>
                      )}
                    </label>
                  ) : (
                    <div className="form-group">
                      <label className="form-label" style={{ fontWeight: 'bold' }}>
                        Descrição *
                      </label>
                      <input
                        type="text"
                        value={item.descricao}
                        onChange={(e) => updateNewItem(item.id, 'descricao', e.target.value)}
                        className="form-control"
                        placeholder="Digite a descrição do item..."
                      />
                    </div>
                  )}

                  {item.id <= 0 && (
                    <>
                      <div className="form-group">
                        <label className="form-label">Categoria *</label>
                        <input
                          type="text"
                          value={item.categoria}
                          onChange={(e) => updateNewItem(item.id, 'categoria', e.target.value)}
                          className="form-control"
                          placeholder="Digite a categoria do item..."
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">
                          <input
                            type="checkbox"
                            checked={item.obrigatorio}
                            onChange={(e) => updateNewItem(item.id, 'obrigatorio', e.target.checked)}
                            style={{ marginRight: '8px' }}
                          />
                          Obrigatório
                        </label>
                      </div>
                    </>
                  )}

                  {item.id > 0 && (
                    <div style={{ color: '#7f8c8d', fontSize: '0.8rem', marginBottom: '12px' }}>
                      Categoria: {item.categoria}
                    </div>
                  )}

                  <div className="form-group">
                    <label className="form-label">Status *</label>
                    <select
                      value={item.valor}
                      onChange={(e) => {
                        if (item.id > 0) {
                          handleItemChange(item.id, 'valor', e.target.value);
                        } else {
                          updateNewItem(item.id, 'valor', e.target.value);
                        }
                      }}
                      className="form-control"
                    >
                      <option value="aprovado">Aprovado</option>
                      <option value="reprovado">Reprovado</option>
                      <option value="pendente">Pendente</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      Observação {item.valor === 'reprovado' && <span style={{ color: '#e74c3c' }}>*</span>}
                    </label>
                    <textarea
                      value={item.observacao || ''}
                      onChange={(e) => {
                        if (item.id > 0) {
                          handleItemChange(item.id, 'observacao', e.target.value);
                        } else {
                          updateNewItem(item.id, 'observacao', e.target.value);
                        }
                      }}
                      className="form-control"
                      rows={2}
                      placeholder="Observação específica para este item..."
                      required={item.valor === 'reprovado'}
                    />
                  </div>
                </div>

                {item.id < 0 && (
                  <div style={{
                    marginTop: '8px',
                    padding: '4px 8px',
                    background: '#d4edda',
                    color: '#155724',
                    borderRadius: '4px',
                    fontSize: '0.8rem',
                    textAlign: 'center'
                  }}>
                    <i className="fas fa-plus-circle"></i> NOVO ITEM
                  </div>
                )}
              </div>
            ))}
          </div>

          {removedItems.length > 0 && (
            <div style={{ marginTop: '16px', padding: '12px', background: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '4px' }}>
              <strong>⚠️ ATENÇÃO:</strong> {removedItems.length} item(s) serão removidos ao salvar.
              {newItems.length > 0 && ` ${newItems.length} item(s) serão adicionados.`}
            </div>
          )}

          {newItems.length > 0 && (
            <div style={{ marginTop: '16px', padding: '12px', background: '#d1ecf1', border: '1px solid #bee5eb', borderRadius: '4px' }}>
              <strong>ℹ️ INFO:</strong> {newItems.length} novo(s) item(s) serão adicionados ao salvar.
              {removedItems.length > 0 && ` ${removedItems.length} item(s) serão removidos.`}
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/checklists')}
            disabled={isSaving}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <span className="loading"></span>
                Atualizando...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                Atualizar Checklist
              </>
            )}
          </button>
        </div>
      </form>

      {/* Modal para adicionar itens padrão */}
      {showAddItemModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #ddd',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, color: '#2c3e50' }}>Adicionar Itens do Checklist</h3>
              <button
                onClick={() => setShowAddItemModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#7f8c8d'
                }}
              >
                ×
              </button>
            </div>

            <div style={{
              padding: '20px',
              flex: 1,
              overflowY: 'auto'
            }}>
              {isLoadingDefaultItems ? (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  <div className="loading-spinner"></div>
                  <p>Carregando itens padrão...</p>
                </div>
              ) : defaultItems.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '50px', color: '#7f8c8d' }}>
                  <i className="fas fa-info-circle" style={{ fontSize: '2rem', marginBottom: '10px' }}></i>
                  <p>Nenhum item padrão disponível</p>
                </div>
              ) : (
                <ChecklistItemSelectorEdit
                  items={defaultItems}
                  existingItemIds={formData && defaultItems.length > 0 ? getUsedDefaultItemIds(formData.itens, defaultItems) : []}
                  onSelectionChange={(selectedItems) => {
                    addSelectedItems(selectedItems);
                    setShowAddItemModal(false); // Fecha o modal automaticamente
                  }}
                />
              )}
            </div>

            <div style={{
              padding: '20px',
              borderTop: '1px solid #ddd',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px'
            }}>
              <button
                onClick={() => setShowAddItemModal(false)}
                className="btn btn-secondary"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </FormLayout>
  );
};

// Componente auxiliar para seleção de itens
interface ChecklistItemSelectorProps {
  items: ChecklistItem[];
  existingItemIds?: number[];
  onSelectionChange: (selectedItems: ChecklistItem[]) => void;
}

const ChecklistItemSelectorEdit: React.FC<ChecklistItemSelectorProps> = ({ items, existingItemIds = [], onSelectionChange }) => {
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());

  // Quando existingItemIds muda, remove automaticamente os itens que já foram adicionados
  React.useEffect(() => {
    setSelectedItems(prev => {
      const newSelected = new Set(prev);
      Array.from(newSelected).forEach(itemId => {
        if (existingItemIds.includes(itemId)) {
          newSelected.delete(itemId);
        }
      });
      return newSelected;
    });
  }, [existingItemIds]);

  const toggleItemSelection = (itemId: number) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const handleAddSelected = () => {
    const availableItems = items.filter(item => !existingItemIds.includes(item.id));
    const selected = availableItems.filter(item => selectedItems.has(item.id));
    onSelectionChange(selected);
    setSelectedItems(new Set());
  };

  const getAvailableItems = () => {
    return items.filter(item => !existingItemIds.includes(item.id));
  };

  const availableItems = getAvailableItems();

  const groupItemsByCategory = () => {
    const grouped: { [key: string]: ChecklistItem[] } = {};
    availableItems.forEach(item => {
      if (!grouped[item.categoria]) {
        grouped[item.categoria] = [];
      }
      grouped[item.categoria].push(item);
    });
    return grouped;
  };

  const groupedItems = groupItemsByCategory();
  const allSelected = availableItems.every(item => selectedItems.has(item.id));

  const handleSelectAll = () => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);

      if (allSelected) {
        // Desmarcar todos disponíveis
        availableItems.forEach(item => {
          newSet.delete(item.id);
        });
      } else {
        // Selecionar todos disponíveis
        availableItems.forEach(item => {
          newSet.add(item.id);
        });
      }

      return newSet;
    });
  };

  return (
    <div>
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button
          type="button"
          onClick={handleSelectAll}
          disabled={availableItems.length === 0}
          style={{
            padding: '10px 16px',
            backgroundColor: availableItems.length > 0 ? '#007bff' : '#ccc',
            border: 'none',
            borderRadius: '6px',
            color: 'white',
            cursor: availableItems.length > 0 ? 'pointer' : 'not-allowed',
            fontSize: '14px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          title={allSelected ? 'Desmarcar todos os itens exibidos' : 'Selecionar todos os itens exibidos'}
        >
          <i className={`fas fa-${allSelected ? 'check-double' : 'check-square'}`}></i>
          {allSelected ? 'Desmarcar Todos' : 'Selecionar Todos'}
          <span style={{ fontWeight: 'normal' }}>
            ({availableItems.length} itens)
          </span>
        </button>

        <button
          type="button"
          onClick={handleAddSelected}
          disabled={selectedItems.size === 0}
          style={{
            padding: '10px 20px',
            backgroundColor: selectedItems.size > 0 ? '#04C64F' : '#ccc',
            border: 'none',
            borderRadius: '6px',
            color: 'white',
            cursor: selectedItems.size > 0 ? 'pointer' : 'not-allowed',
            fontWeight: '600'
          }}
        >
          Adicionar Selecionados ({selectedItems.size})
        </button>
      </div>

      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
        {Object.entries(groupedItems).map(([categoria, categoriaItems]) => (
          <div key={categoria} style={{ marginBottom: '20px' }}>
            <h4 style={{
              marginBottom: '10px',
              color: '#2c3e50',
              borderBottom: '1px solid #ddd',
              paddingBottom: '5px'
            }}>
              {categoria} ({categoriaItems.length})
            </h4>

            <div style={{ display: 'grid', gap: '10px' }}>
              {categoriaItems.map(item => {
                const isAlreadySelected = existingItemIds.includes(item.id);
                return (
                  <div
                    key={item.id}
                    onClick={() => toggleItemSelection(item.id)}
                    style={{
                      padding: '12px',
                      border: selectedItems.has(item.id)
                        ? '2px solid #04C64F'
                        : isAlreadySelected
                          ? '2px solid #f39c12'
                          : '1px solid #ddd',
                      borderRadius: '6px',
                      cursor: isAlreadySelected ? 'not-allowed' : 'pointer',
                      backgroundColor: selectedItems.has(item.id)
                        ? '#f8fffe'
                        : isAlreadySelected
                          ? '#fff3cd'
                          : 'white',
                      opacity: isAlreadySelected ? 0.7 : 1,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {isAlreadySelected ? (
                        <input
                          type="checkbox"
                          checked={true}
                          disabled={true}
                          style={{ cursor: 'not-allowed' }}
                        />
                      ) : (
                        <input
                          type="checkbox"
                          checked={selectedItems.has(item.id)}
                          onChange={() => {}} // Manipulado pelo onClick do container
                          style={{ cursor: 'pointer' }}
                        />
                      )}
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontWeight: '500',
                          color: isAlreadySelected ? '#f39c12' : '#2c3e50'
                        }}>
                          {item.descricao}
                          {item.obrigatorio && (
                            <span style={{ color: '#e74c3c', marginLeft: '8px' }}>*</span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#7f8c8d', marginTop: '2px' }}>
                          {item.categoria}
                          {isAlreadySelected && (
                            <span style={{ color: '#f39c12', fontWeight: 'bold', marginLeft: '8px' }}>
                              Já adicionado
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ChecklistEdit;
